import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useState } from "react";
import { Linking, Platform, StyleSheet } from "react-native";

import { Box, Heading, Pressable, Text, TextLink } from "../../../../../../mads";
import { Clipboard } from "../../../../../../mads/icons/Clipboard";
import { Clock } from "../../../../../../mads/icons/Clock";
import { Location as LocationIcon } from "../../../../../../mads/icons/Location";
import { Salary } from "../../../../../../mads/icons/Salary";
import { LinearGradient } from "../../../../../../shared/components/LinearGradient/linear-gradient";
import { useI18n } from "../../../../../../shared/I18n";
import { useViewjobProctorAllocation } from "../../../../../proctor/__generated__/ViewjobProctorContext";
import { MobVjJobSummaryTst } from "../../../../../proctor/__generated__/ViewjobProctorSpecifications";
import { DwellTrackedSection } from "../../../../context/DwellTrackedSection";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";
import { useIsDesktop } from "../../../../context/ViewJobScreenDisplay";
import {
  useLocationDisplayFromContext,
  useTrueJobLocation,
  LocationDisplayType,
  type LocationDisplay,
} from "../../../../utils/locationUtils";
import type { Location as LocationType } from "../../../../viewjobOneGraphModel";
import { VJNegativeSpacing } from "../../ProfileInsights/components/common/VJNegativeSpacing";
import { CommuteSection } from "./CommuteSection";
import { JobDutiesDisclaimerPopover } from "./JobDutiesDisclaimerPopover";

export interface StructuredJobSummaryProps {
  /** The structured job summary model from viewjob */
  structuredJobSummaryModel?: jobinfowrapper.jobinfo.StructuredDataJobSummaryModel;
  /** Job duties description text */
  jobDuties?: string;
}

const JOB_DUTIES_PREVIEW_HEIGHT = 80;

function getLocationDisplayText(
  display: LocationDisplay,
  remoteWorkText?: string,
  streetAddress?: string,
): string | undefined {
  switch (display.type) {
    case LocationDisplayType.ADDRESS_WITH_REMOTE:
    case LocationDisplayType.LOCATION_WITH_REMOTE:
      return `${remoteWorkText} in ${display.resolvedLocation}`;
    case LocationDisplayType.STREET_ADDRESS:
      return streetAddress;
    case LocationDisplayType.LOCATION_ONLY:
      return display.resolvedLocation;
    case LocationDisplayType.REMOTE_ONLY:
      return remoteWorkText;
    default:
      return undefined;
  }
}

function getLocationLink(streetAddress?: string, location?: LocationType) {
  if (streetAddress) {
    return streetAddress;
  }

  const { city, admin1Code, postalCode } = location ?? {};

  if (city && admin1Code && postalCode) {
    return `${city}, ${admin1Code} ${postalCode}`;
  }
}

export function StructuredJobSummary({
  structuredJobSummaryModel,
  jobDuties,
}: StructuredJobSummaryProps) {
  const [showAllBenefits, setShowAllBenefits] = useState(false);
  const [showAllJobDuties, setShowAllJobDuties] = useState(false);
  const proctorValue = useViewjobProctorAllocation(MobVjJobSummaryTst).value;
  const hideJobDetailsHeader = proctorValue === 2;
  const showPayInformationNotProvided = proctorValue >= 4;
  const showJobDuties = proctorValue === 5;
  const gt = useI18n();
  const viewjobOneGraphModel = useViewjobOneGraphModel();
  const isDesktop = useIsDesktop();

  const display = useLocationDisplayFromContext({
    shouldInlineRemoteText: true,
    preferAddressWithRemote: true,
  });

  const trueJobLocation = useTrueJobLocation();
  const hideCommuteForMLA = !!trueJobLocation;
  const streetAddress = viewjobOneGraphModel?.job?.location?.formattedStreetAddress;
  const jobLatitude = viewjobOneGraphModel?.job?.location?.jobLatitude;
  const jobLongitude = viewjobOneGraphModel?.job?.location?.jobLongitude;
  // Skip the CommuteSection entirely when MLA is taking over the location
  // slot, or when the job lacks lat/lon + a precise street address
  const shouldShowCommuteEstimate =
    !hideCommuteForMLA && jobLatitude != null && jobLongitude != null && !!streetAddress;

  if (!structuredJobSummaryModel) {
    return null;
  }

  const remoteWorkText = viewjobOneGraphModel?.job?.remoteWorkModel?.text;

  const locationDisplayText = getLocationDisplayText(display, remoteWorkText, streetAddress);
  const locationLink = getLocationLink(streetAddress, viewjobOneGraphModel?.job?.location);

  const openAddressInMaps = () => {
    if (!locationLink) return;

    const encoded = encodeURIComponent(locationLink);

    const url = Platform.select({
      ios: `maps:0,0?q=${encoded}`,
      android: `geo:0,0?q=${encoded}`,
      web: `https://www.google.com/maps/search/?api=1&query=${encoded}`,
    });

    if (url) {
      void Linking.openURL(url).catch(() => {});
    }
  };

  const renderPayAndBenefits = () => {
    const salary = structuredJobSummaryModel.salary;
    const benefits = structuredJobSummaryModel.benefits;

    if (!salary && (!benefits || benefits.length === 0) && !showPayInformationNotProvided)
      return null;

    const BENEFITS_THRESHOLD = 8;
    const visibleBenefits = showAllBenefits ? benefits : benefits?.slice(0, BENEFITS_THRESHOLD);
    const remainingBenefitsCount = (benefits?.length || 0) - BENEFITS_THRESHOLD;

    return (
      <Box sx={styles.section}>
        <Box sx={styles.tileSectionRow}>
          <Box sx={styles.iconContainer}>
            <Salary size="sm" variant="filled" fill={colors.foregroundStrong} />
          </Box>
          <Box sx={styles.contentContainer}>
            {salary && (
              <DwellTrackedSection section="pay">
                <Box
                  accessibilityLabel={salary.replace(
                    / - /g,
                    ` ${gt.pgettext("salary range separator", "to")} `,
                  )}
                  accessible={true}
                >
                  <Text level={3} sx={styles.tileValueText}>
                    {salary}
                  </Text>
                </Box>
              </DwellTrackedSection>
            )}
            {!salary && showPayInformationNotProvided && (
              <DwellTrackedSection section="pay">
                <Text level={3} sx={styles.tileValueText}>
                  {gt.gettext("Pay information not provided")}
                </Text>
              </DwellTrackedSection>
            )}
            {benefits && benefits.length > 0 && (
              <DwellTrackedSection section="benefits">
                <>
                  <Text variant="contrast" level={3}>
                    {visibleBenefits?.join(", ")}
                  </Text>
                  {!showAllBenefits && benefits.length > BENEFITS_THRESHOLD && (
                    <Pressable
                      onPress={() => setShowAllBenefits(true)}
                      sx={styles.showMoreButton}
                      accessibilityRole="button"
                    >
                      <Text sx={styles.showMoreText}>
                        {gt.gettext("Show {0} more", [remainingBenefitsCount])}
                      </Text>
                    </Pressable>
                  )}
                </>
              </DwellTrackedSection>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderJobTypeAndShiftSection = () => {
    const jobTypeAndShiftSection = structuredJobSummaryModel.jobTypeAndShiftSectionApp;
    if (
      !jobTypeAndShiftSection ||
      (!jobTypeAndShiftSection.jobType && !jobTypeAndShiftSection.shiftAndSchedule)
    )
      return null;

    return (
      <Box sx={styles.section}>
        <Box sx={styles.tileSectionRow}>
          <Box sx={styles.iconContainer}>
            <Clock size="sm" variant="filled" fill={colors.foregroundStrong} />
          </Box>
          <Box sx={styles.contentContainer}>
            <DwellTrackedSection section="jobType">
              {jobTypeAndShiftSection.jobType && (
                <Text level={3} sx={styles.tileValueText}>
                  {jobTypeAndShiftSection.jobType.join(", ")}
                </Text>
              )}
              {jobTypeAndShiftSection.shiftAndSchedule && (
                <Text variant="contrast" level={3}>
                  {jobTypeAndShiftSection.shiftAndSchedule.join(", ")}
                </Text>
              )}
            </DwellTrackedSection>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderJobDuties = () => {
    if (!showJobDuties || !jobDuties) return null;

    return (
      <Box sx={styles.section}>
        <Box sx={styles.tileSectionRow}>
          <Box sx={styles.iconContainer}>
            <Clipboard size="sm" variant="filled" fill={colors.foregroundStrong} />
          </Box>
          <Box sx={styles.contentContainer}>
            <Box sx={styles.jobDutiesHeadingRow}>
              <Heading level={3}>{gt.gettext("Duties summarized by AI")}</Heading>
              <JobDutiesDisclaimerPopover />
            </Box>
            <Box sx={showAllJobDuties ? undefined : styles.jobDutiesCollapsedContainer}>
              <Text variant="contrast" level={3}>
                {jobDuties}
              </Text>
              {!showAllJobDuties && (
                <LinearGradient
                  colors={["rgba(255, 255, 255, 0.0)", "rgba(255, 255, 255, 1.0)"]}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 0, y: 1 }}
                  style={styles.jobDutiesFade}
                  pointerEvents="none"
                />
              )}
            </Box>
            {!showAllJobDuties && (
              <Pressable
                onPress={() => setShowAllJobDuties(true)}
                sx={styles.showMoreButton}
                accessibilityRole="button"
              >
                <Text sx={styles.showMoreText}>{gt.gettext("Show more")}</Text>
              </Pressable>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      testID="structured-job-summary"
      sx={[styles.container, isDesktop && styles.desktopSectionSpacing]}
    >
      {!hideJobDetailsHeader && (
        <Heading sx={styles.title} level={4}>
          {gt.gettext("Job details")}
        </Heading>
      )}

      {renderPayAndBenefits()}

      {renderJobTypeAndShiftSection()}

      {renderJobDuties()}

      {locationDisplayText && (
        <Box sx={styles.section}>
          <Box sx={styles.tileSectionRow}>
            <Box sx={styles.iconContainer}>
              <LocationIcon size="sm" variant="filled" fill={colors.foregroundStrong} />
            </Box>
            <Box sx={styles.contentContainer}>
              <DwellTrackedSection section="location">
                {locationLink ? (
                  <TextLink
                    level={3}
                    onPress={openAddressInMaps}
                    aria-label={gt.gettext("Open {0} in maps", [locationDisplayText])}
                  >
                    {gt.gettext("{0}", [locationDisplayText])}
                  </TextLink>
                ) : (
                  <Text level={3} sx={styles.tileValueText}>
                    {gt.gettext("{0}", [locationDisplayText])}
                  </Text>
                )}
                {hideCommuteForMLA && (
                  <Text sx={styles.disclaimerText}>
                    {gt.gettext("Actively hiring candidates from your area")}
                  </Text>
                )}
              </DwellTrackedSection>
            </Box>
          </Box>
        </Box>
      )}

      {shouldShowCommuteEstimate && (
        <CommuteSection commuteTimeMinutes={structuredJobSummaryModel.commuteTimeMinutes} />
      )}

      <VJNegativeSpacing hasDivider={true} />
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    overflow: "visible",
  },
  desktopSectionSpacing: {
    marginBlockStart: spacing.s5,
  },
  title: {
    marginBottom: 16,
  },
  section: {
    marginBottom: 16,
    overflow: "visible",
  },
  tileSectionRow: {
    flexDirection: "row",
    gap: 8,
    alignItems: "flex-start",
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: 2,
  },
  contentContainer: {
    flex: 1,
    gap: 8,
    overflow: "visible",
  },
  tileValueText: {
    fontWeight: "bold",
    color: colors.foregroundStrong,
  },
  disclaimerText: {
    color: colors.foregroundStrong,
    fontSize: 14,
  },
  showMoreButton: {
    marginTop: 4,
    justifyContent: "center",
    alignItems: "flex-start",
  },
  showMoreText: {
    fontSize: 16,
    color: "#2d2d2d",
    fontWeight: "500",
    borderWidth: 0,
    borderBottomWidth: 1,
    borderStyle: "dotted",
  },
  jobDutiesHeadingRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  jobDutiesCollapsedContainer: {
    height: JOB_DUTIES_PREVIEW_HEIGHT,
    overflow: "hidden",
    position: "relative",
  },
  jobDutiesFade: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 50,
  },
});
